<!-- eslint-disable @typescript-eslint/no-unused-expressions -->
<script setup lang="ts">
import { ref, inject } from 'vue';
import type { ComputedRef } from 'vue';
import { useQuasar } from 'quasar';
import { useRouter } from 'vue-router';
import { useStrapiPut } from 'src/shared/composables/useStrapiApi';
import BCard from 'src/shared/components/base/BCard/BCard.vue';
import ProfileSettingList from 'modules/Setting/components/ProfileSettingList/ProfileSettingList.vue';
import ProfileSettingForm from 'modules/Setting/components/ProfileSettingForm/ProfileSettingForm.vue';
import BErrorBanner from 'src/shared/components/base/BErrorBanner/BErrorBanner.vue';
import { removeIds } from 'src/shared/composables/usePiniaOrm';
import type { Profile } from 'src/modules/User/models/Profile';
import { useCurrentUser } from 'shared/composables/useCurrentUser';
import { useRepo } from 'pinia-orm';
import { Profile as ProfileModel } from 'src/modules/User/models/Profile';

const $q = useQuasar();
const currentUserId = inject('currentUserId') as ComputedRef<number>;
const currentUser = useCurrentUser(currentUserId.value, 'all');

const router = useRouter();
const profileRepo = useRepo(ProfileModel);

const isEdit = ref(false);
const isLoading = ref(false);

const profileDocumentId = ref<string | undefined>(
  currentUser.value?.profile?.documentId,
);

async function handleProfile() {
  isEdit.value = false;
  isLoading.value = true;

  const profile = await upsertMyProfile();
  piniaOrmUpdateProfile(profile);

  isLoading.value = false;
}

function piniaOrmUpdateProfile(profile: { data: Profile } | boolean) {
  if (typeof profile === 'boolean' || !profile?.data) return;

  profileRepo.save({
    ...profile.data,
    user_id: currentUser.value?.id ?? null,
  });
  profileDocumentId.value = profile.data.documentId;
}

async function upsertMyProfile(): Promise<boolean | { data: Profile }> {
  if (!currentUser.value) return false;

  const { execute, error, data } = useStrapiPut(
    'users-permissions/users/me/profile',
    {
      // firstname/lastname go to the user, contact stays on profile; the backend
      // upsert splits them. See Phase 8 (canonical name on user).
      data: {
        firstname: currentUser.value.firstname,
        lastname: currentUser.value.lastname,
        ...removeIds(currentUser.value.profile ?? {}, [
          'documentId',
          'id',
          'user_id',
        ]),
      },
    },
  );

  await execute();

  return !error.value && data.value;
}

function cancleUpdate() {
  isEdit.value = false;
}
</script>

<template>
  <BCard
    v-if="
      ['parent', 'school_admin', 'teacher', 'student'].includes(
        currentUser?.role?.type || '',
      )
    "
    size="sm"
    class="rounded-borders-xl profile-card"
    :class="$q.dark.isActive ? 'shadow-card-dark' : 'shadow-card bg-white'"
    :dark="$q.dark.isActive"
  >
    <q-inner-loading :showing="isLoading">
      <q-spinner-gears
        size="80px"
        color="primary"
      />
    </q-inner-loading>
    <q-card-section
      v-if="currentUser"
      class="profile-card__section"
    >
      <ProfileSettingList
        v-if="currentUser && !isEdit"
        v-model="currentUser"
        @edit-profile="isEdit = true"
      />
      <ProfileSettingForm
        v-else
        v-model="currentUser"
        class="profile-card__form"
        @submit="handleProfile"
        @close="cancleUpdate"
      />
    </q-card-section>
    <q-card-section v-else>
      <BErrorBanner @ok="router.push({ name: 'dashboard' })">
        {{ $t('UNFORTUNATLY_WE_COULD_NOT_LOAD_THE_USER_DATA') }}
      </BErrorBanner>
    </q-card-section>
  </BCard>
</template>

<style scoped>
.profile-card {
  height: 100%;
}

.profile-card__section {
  height: 100%;
}

.profile-card__form {
  max-height: min(70vh, 560px);
  overflow-y: auto;
  padding-right: 4px;
}
</style>
